import { useSelector } from 'react-redux';

import { adaptPublicAttributesToUI } from 'src/adapters/storeToUI/adaptPublicAttributesToUI';
import { getProductBadge } from 'src/components/layouts/PDP/getProductBadge';
import { REVIEWS_URL_FRAGMENT_ID } from 'src/fragmentIDs';
import { selectAccessibility } from 'src/redux/accessibility/selectors';
import { selectOptions } from 'src/redux/basket/selectors';
import { selectBreadcrumbsLinks } from 'src/redux/breadcrumbs/selectors';
import { selectOfferLabelContent } from 'src/redux/content/selectors';
import { selectDeliveryZones } from 'src/redux/delivery/selectors';
import { selectFavouriteProduct } from 'src/redux/favourites/selectors';
import { selectProduct, selectProductMedia } from 'src/redux/product/selectors';
import { getMinimumOptionsCost } from 'src/redux/product/utils/getMinimumOptionsCost';
import { getOptionsCostRange } from 'src/redux/product/utils/getOptionsCostRange';
import { selectProductReviews } from 'src/redux/reviews/selectors';
import { PRODUCT_CODE_TITLE } from '../PDP/copy';
import type { PDPViewModel } from '../PDP/types';
import type { PartnerViewModel } from './usePartnerViewModel';
import { usePartnerViewModel } from './usePartnerViewModel';
import type { ProductFeaturesViewModel } from './useProductFeaturesViewModel';
import { useProductFeaturesViewModel } from './useProductFeaturesViewModel';

export interface PDPLayoutData {
  deliveryOptionsLoaded: boolean;
  hasProductOptions: boolean;
  partner: PartnerViewModel;
  productFeatures: ProductFeaturesViewModel;
  similarProductsServingConfig: 'pdp_similar_products' | 'pdp-similar-products-bury-sale';
  viewModel: PDPViewModel;
}

export const usePDPViewModel = (): PDPLayoutData => {
  const product = useSelector(selectProduct);
  const breadcrumbs = useSelector(selectBreadcrumbsLinks);
  const reviews = useSelector(selectProductReviews);
  const options = useSelector(selectOptions);
  const accessibility = useSelector(selectAccessibility);
  const offerLabelContent = useSelector(selectOfferLabelContent);
  const deliveryZones = useSelector(selectDeliveryZones);
  const media = useSelector(selectProductMedia);
  const favouriteProduct = useSelector(selectFavouriteProduct);
  const partner = usePartnerViewModel();
  const productFeatures = useProductFeaturesViewModel();

  const optionsCostRange = getOptionsCostRange(options, product.price.currency);
  const minimumOptionsCost = getMinimumOptionsCost({
    currency: product.price.currency,
    options,
    personalisable: product.specialFeatures.personalisable,
  });
  const priceIsMinimum = optionsCostRange.maxOptional + optionsCostRange.maxRequired > 0;

  const offerLabelAllowedForProduct =
    !offerLabelContent?.showOnlyForDiscountedProducts || product.onSale;
  const offerLabel =
    offerLabelContent && offerLabelAllowedForProduct
      ? {
          countdown: offerLabelContent.countdown,
          message: offerLabelContent.message,
          promoCode: offerLabelContent.promoCode,
          showInRed: offerLabelContent.showInRed,
        }
      : null;

  return {
    deliveryOptionsLoaded: Boolean(deliveryZones),
    hasProductOptions: options.length > 0,
    partner,
    productFeatures,
    similarProductsServingConfig: product.onSale
      ? 'pdp_similar_products'
      : 'pdp-similar-products-bury-sale',
    viewModel: {
      badge: getProductBadge(product),
      breadcrumbs,
      images: {
        favouriteProduct,
        media,
        productCode: product.code,
      },
      offerLabel,
      pageLiveRegionText: accessibility.statusMessage,
      price: {
        minimumOptionsCost,
        preSalePrices: product.preSalePrices,
        price: product.price,
        priceIsMinimum,
        salePercentage: product.salePercentage || 0,
        salePercentageInRed: Boolean(offerLabel?.showInRed),
      },
      productInformation: {
        dimensions: product.dimensions || undefined,
        ingredientsFileURLPath: product.ingredients ? product.ingredients.rel! : undefined,
        materials: product.materials || undefined,
        otherInformation: product.otherInformation || '',
        variationsAvailable: product.variationsAvailable || undefined,
      },
      productReviews: {
        count: reviews.numberOfReviews || 0,
        href: `#${REVIEWS_URL_FRAGMENT_ID}`,
        rating: reviews.averageProductRating || null,
      },
      publicAttributes: [
        ...(product.publicAttributes ? adaptPublicAttributesToUI(product.publicAttributes) : []),
        { key: 'productCode', title: PRODUCT_CODE_TITLE, values: [String(product.code)] },
      ],
      title: product.title,
      description: product.description,
    },
  };
};
